import {Component, h, Prop, Element, State, Watch, Event, type EventEmitter, Listen} from '@stencil/core';
import {translations} from './translations';
import {widgetStatistics} from './faq-statistics';
import type {AnalyticsGenericEvent} from '../../../utils/analytics/AnalyticsGenericEvent';
import { makeLogger } from '../../../logger/logger';
import type {TabAnalyticsEvent} from '../../result-page/tabs-header/TabAnalyticsEvent';
import { authenticationHeaders} from '../../guided/subcomponents/utils';
import { appendSectionToTabName, transformTabName } from '../../result-page/search-tab/subcomponents/dashboard-tab-view/utils/utils';
import faqWidgetStore from './faq.component.store';
import {getSiteSearchCategory} from '../../analytics-reporting/util';

const { info, warn, err } = makeLogger({
  pathToCurrentFile: 'components/widgets/faq/faq.component.tsx',
  loggersList: ['info', 'warn', 'err']
});

type Headers = Record<string, string>;

@Component({
  tag: 'uiaas-search-widget-faq',
  styleUrl: 'faq.scss',
  shadow: true
})
export class WidgetFaq {
  private rightToLeft: boolean;

  @Prop() readonly country: string;
  @Prop() readonly language: string;
  @Prop() readonly appName: string;

  /**
  * Enables right-to-left-view
  */
  @Prop() readonly rtl: boolean;

  @Prop({mutable: true}) labelProvider?: (a, b) => string;

  @Prop({attribute: 'term', reflect: true}) readonly termAttribute: string;

  @State() _term;

  @Prop({attribute: 'ignore-urls'}) readonly ignoreUrls: string;

  @Prop() readonly bearer?: string;

  @Prop() readonly maxItems: number = 4;
  @Prop() readonly threshold: number = 50;

  @Prop() readonly apiEndpoint: string;
  @Prop() readonly apigeeAuth: string;
  @Prop() readonly apigeeTokenUrl: string;

  @Element() el: HTMLElement;

  @Prop({mutable:true}) tabName: string;

  @Prop({mutable: true}) domain: string;

  /**
  * Number of result for dashboard FAQ variant
  */
  @Prop() readonly tabCount?: number;

  /**
  * Target tab name for dashboard variant
  */
  @Prop() readonly targetTab?: string;

  /**
  * Data from searcher to populate FAQ in Dashboard view variant
  */
  @Prop() readonly dashboardSearcherData?: Array<FAQ>;

  /**
  * Text for tab button on dashboart variant
  */
  @Prop() readonly dashboardButtonText?: string;

  /**
  * Text for tab subtitle on dashboart variant
  */
  @Prop() readonly dashboardSubTitleText?: string;

  @Prop() readonly isDashboardView?: boolean;

  private activeTab: string;

  @Watch('termAttribute')
  termWatch(newValue, oldValue) {
      if (oldValue !== newValue) {
        this._term = newValue;
        this._answers = [];
        this._eventSent = false;

        return this.reset();
      }
  }

  @Event({
    eventName: 'search:widgetEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    statisticsEventEmitter: EventEmitter<AnalyticsGenericEvent>;

  @Listen('search:identifyActiveTab', { target: 'document' })
  identifyActiveTab(event) {
      const eventActiveTab = event.detail?.activeTab;
      const eventTabCount = event.detail?.count;
      if (eventActiveTab === this.tabName && eventTabCount) {
        this.activeTab = eventActiveTab;
      }
  }

  @Listen('search:tabClick', { target: 'document' })
  tabClickEventHandler(event: TabAnalyticsEvent & CustomEvent) {
      const eventTabName = event.detail?.tabName;
      const eventTabCount = event.detail?.tabCount;
      const labelTabName = 'tabName:' + eventTabName;

      if(this._answers?.length && eventTabCount && this.tabName === eventTabName) {
        this.emitSearchStatisticsEvent(widgetStatistics.sendPresentationStatisticsEvent(labelTabName, true));
      }
  }

  private appConfig = {
    'apc' : {
      appName: 'apc',
      domain: 'https://www.apc.com/',
      query: window.location.search.split('q=').pop().replace(/\+/g, ' ')
    },
    'se' : {
      appName: 'se',
      domain: 'https://www.se.com/',
      query: window.location.pathname.split('search/').pop()
    },
    'default' : {
      appName: 'se',
      domain: 'https://www.se.com/',
      query: window.location.pathname.split('search/').pop()
    }
  };

  private async reset() {
      if (!this || !this._term || this._term.length === 0) {
        return;
      }

      const term = this._term.replace('"', '').replace('\'', '').replace(/&[a-z0-9A-Z];/, '');

      if (this.dashboardSearcherData) return;
      if (term.length < 3) return;

      if (faqWidgetStore.state.term == this._term){
        this._answers = faqWidgetStore.state.answers;
        return;
      } else {
        faqWidgetStore.state.term = this._term;

        const authHeaders = await authenticationHeaders({
          apigeeBasicAuth: this.apigeeAuth,
          tokenUrl: this.apigeeTokenUrl,
        });

        const headers: Headers = {
          'Content-type': 'application/json; charset=utf-8',
          ...authHeaders
        };

        const createUrl = (endpoint: string) => {
            try {
              const url = new URL(endpoint);
              url.searchParams.append('ui-component', 'widget-FAQ');
              return url.href;
            } catch (e) {
              err(`unable to create url from  ${endpoint}`);
              return '';
            }
        };


        const url = createUrl(this.apiEndpoint);

        const request = new Request(url, {
          method: 'POST',
          body: JSON.stringify({
            'country': this.country,
            'language': this.language,
            'forceTerm': false,
            'term': term,
            'pagination': {
              'limit': this.maxItems,
              'offset': 0
            },
            'searcher': `${this.appName ? this.appName : this.appConfig['default'].appName}/faq`
          }),
          headers: headers
        });

        try {
          const response = await fetch(request);
          const json = await response.json();
          if (!json.documents || json.documents.length == 0) {
            this.emitSearchStatisticsEvent(widgetStatistics.sendPresentationStatisticsEvent(this.tabName, false));
            return;
          }
          this._answers = json.documents.map(d => {
              if (!d.relevance) {
                d.relevance = 100;
              }
              return d;
          });
          faqWidgetStore.state.answers = this._answers;
        } catch (e) {
          this.emitSearchStatisticsEvent(widgetStatistics.sendPresentationStatisticsEvent(this.tabName, false));
          err('Failed to fetch FAQ from API', e, {request: request});
        }
      }
  }

  private emitSearchStatisticsEvent = (e: AnalyticsGenericEvent) => {
      this.statisticsEventEmitter.emit({
        ...e,
        searchTerm: this.termAttribute
      });
  };

  //done async, executed once when component is inserted
  /*  It's a lie that it's not used!!! It'a a stencil lifecycle hook! */

  // noinspection JSUnusedGlobalSymbols
  componentDidLoad() {
      this._term = this.termAttribute;
      this.reset();
      faqWidgetStore.onChange('answers', () => this.reset());
  }

  componentWillLoad() {
      const {peopleAlsoAsk, seeMore, viewAll} = translations;

      this.domain = this.appConfig[this.appName] ? this.appConfig[this.appName].domain : this.appConfig['default'].domain;

      if (!this.labelProvider) {
        this.labelProvider = (label, fallback) => {
            const language = this.language?.toLowerCase();
            let translation;

            switch (label) {
            case 'faq.widget.people-also-ask':
              translation = peopleAlsoAsk[language] || fallback;
              break;
            case 'faq.widget.see-more':
              translation = seeMore[language] || fallback;
              break;
            case 'faq.widget.view-all':
              translation = viewAll[language] || fallback;
              break;
            default:
              translation = fallback;
              break;
            }

            if (fallback === translation) {
              warn('Search Rich Widget:FaqComponent -> Missing label for ', label, 'using fallback: ', fallback);
            }

            return translation;
        };
      }
  }

  @State()
  private _eventSent: boolean = false;

  connectedCallback() {
      const closestTabEl = this.el.closest('uiaas-search-tab');
      if(closestTabEl) {
        this.tabName = closestTabEl['tabName'];
      }

      const body = this.el.closest('body');
      const html = this.el.closest('html');

      if(html) {
        const rtl = html.getAttribute('dir');
        if (rtl === 'rtl') {
          this.rightToLeft = true;
        }
      }

      if(body) {
        const rtl = body.getAttribute('dir');
        if (rtl === 'rtl') {
          this.rightToLeft = true;
        }
      }

      if (this.rtl) {
        this.rightToLeft = true;
      }
  }

  private sendEventShow(analyticsTabName: string) {
      if (!this._eventSent && this.activeTab && this.activeTab === this.tabName && (this._answers?.length || this.dashboardSearcherData?.length)) {
        this.emitSearchStatisticsEvent(widgetStatistics.sendPresentationStatisticsEvent(analyticsTabName, true));
        this._eventSent = true;
      }
  }

  private getAnswers(): Array<FAQ> {
      const ignoreUrls = this.getIgnoreUrls();
      const doclist = this._answers
        .filter(doc => doc.relevance && parseFloat(doc.relevance) > this.threshold)
        .map(doc => {
            doc.url = this.domain + this.country + '/' + this.language + '/faqs/' + doc.faqID;

            return doc;
        })
        .filter(d => (ignoreUrls || []).indexOf(d.url) == -1);

      if (doclist.length == 0) {
        info('Search FAQ widget: results have poor quality, not showing the widget');

        return;
      }

      return doclist.map((d) => {
          const location = `${this.language}_${this.country.toUpperCase()}`;
          return {
            answer: d[location]?.description,
            question: d[location]?.title,
            url: d[location]?.openUrl,
            id: d.id,
          }; // (as FAQ)
      });
  }

  @State()
    _answers: Array<any> = []; //eslint-disable-line

  private getIgnoreUrls(): Array<string> | string {
      try {
        if (typeof (this.ignoreUrls) == 'string') {
          try {
            return JSON.parse(this.ignoreUrls);
          } catch (ignored) {
            return this.ignoreUrls;
          }
        } else {
          return this.ignoreUrls;
        }
      } catch (ignored) {
        err('Failed miserably trying to parse', this.ignoreUrls);

        return [];
      }
  }

  private getClickType(e): string {
      if (e.key === 'Enter') {
        return e.key;
      }
      if (e.button === 0) {
        return 'left';
      } else {
        return 'right';
      }
  }


  private handleRedirectEvent(e, analyticsTabName, id, url, position): void {
      const clickType = this.getClickType(e);
      this.emitSearchStatisticsEvent(widgetStatistics.sendRedirectionLearnMoreStatisticsEvent(analyticsTabName, clickType, position, id, url));
  }

  private handleUnfoldEvents(e, analyticsTabName, id, position): void {
      if (e.currentTarget.shadowRoot.activeElement.tagName === 'BUTTON') {
        const isOpened = e.currentTarget.isExpanded;
        this.emitSearchStatisticsEvent(widgetStatistics.sendUnfoldStatisticsEvent(analyticsTabName, position, id, isOpened));
      }
  }

  private cleanText = (htmlString) => {
      return htmlString?.replace(/<span[^>]*>(.*?)<\/span>/gi, '$1');
  };

  render() {
      const dashboardViewClass = this.isDashboardView ? 'faq-widget--dashboard' : '';
      const analyticsTabName = getSiteSearchCategory(
        this.isDashboardView ? appendSectionToTabName(transformTabName(this.tabName), 'faq') : this.tabName,
        this.appName
      );

      this.sendEventShow(analyticsTabName);

      const targetDataForFaq = !this.dashboardSearcherData ? this.getAnswers() : this.dashboardSearcherData;

      if (targetDataForFaq && targetDataForFaq.length > 0) {
        let region = this.country + '/' + this.language;
        if (region === 'ww/en') region = 'us/en';

        return (
          <div
            dir={this.rightToLeft ? 'rtl' : 'auto'}
            class={
              this.tabName ? `faq-widget--uiaas ${dashboardViewClass}` : `faq-widget ${dashboardViewClass}`
            }
          >
            {!this.isDashboardView && <h3
              class="faq-widget__title">{this.labelProvider('faq.widget.people-also-ask', 'Frequently asked questions')}</h3>}
            {this.isDashboardView ? <slot name='dashboard-section-header' /> : ''}

            <qds-web-accordion
              layer="panel"
              level="subsection"
              single="false"
            >
              {targetDataForFaq && targetDataForFaq.length > 0 ? targetDataForFaq.map((v, index) => {
                  const text = this.cleanText(v.question);

                  return (
                    <qds-web-accordion-item
                      key={index}
                      text={text}
                      divider={index + 1 < targetDataForFaq.length}
                      onClick={(e) => this.handleUnfoldEvents(e, analyticsTabName, v.id, index)}
                      onKeyDown={e => {
                          if (e.key === 'Enter') {
                            this.handleUnfoldEvents(e, analyticsTabName, v.id, index);
                          }
                      }}
                    >
                      <p innerHTML={v.answer} class="faq-answer-text"/>
                      <div class='more-link'>
                        <qds-standalone-link
                          href={v.url}
                          importance="emphasized"
                          onMouseDown={(e) => {
                              this.handleRedirectEvent(e, analyticsTabName, v.id, v.url, index);}}
                          onKeyDown={e => {
                              if (e.key === 'Enter') {
                                this.handleRedirectEvent(e, analyticsTabName, v.id, v.url, index);
                              }
                          }}>
                          {translations.seeMore[this.language] ? translations.seeMore[this.language] : 'Learn more'}
                        </qds-standalone-link>
                      </div>
                    </qds-web-accordion-item>
                  );
              }) : <span class="no-faqs-to-show"/>}
            </qds-web-accordion>
          </div>);
      } else {
        return <span style={{'display': 'none'}}>"no answers to show faq widget"</span>;
      }
  }
}
